import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import { CircleEvent } from "@/types/circle";
import { toast } from "sonner";

interface CreateCircleEventInput {
  title: string;
  description?: string;
  venue: string;
  start_time: string;
  end_time: string;
  capacity: number;
  credit_cost: number;
  image_url?: string;
  whatsapp_group?: string;
  ticket_booking_url?: string;
  latitude?: number;
  longitude?: number;
}

export function useCircleHostEvents() {
  const { userId } = useAuth();
  const queryClient = useQueryClient();

  const { data: hostedEvents, isLoading, error } = useQuery({
    queryKey: ["circle-host-events", userId],
    queryFn: async () => {
      if (!userId) return [];

      const { data, error } = await supabase
        .from("circle_events")
        .select("*")
        .eq("host_id", userId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as CircleEvent[];
    },
    enabled: !!userId,
  });

  const createEvent = useMutation({
    mutationFn: async (input: CreateCircleEventInput) => {
      if (!userId) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("circle_events")
        .insert({
          ...input,
          host_id: userId,
          status: "pending",
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["circle-host-events"] });
      queryClient.invalidateQueries({ queryKey: ["circle-events"] });
      toast.success("Event created! Awaiting admin approval.");
    },
    onError: (error) => {
      console.error("Error creating circle event:", error);
      toast.error("Failed to create event");
    },
  });

  const cancelEvent = useMutation({
    mutationFn: async ({ eventId, reason }: { eventId: string; reason?: string }) => {
      if (!userId) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("circle_events")
        .update({
          status: "cancelled",
          cancelled_at: new Date().toISOString(),
          cancelled_reason: reason || null,
        })
        .eq("id", eventId)
        .eq("host_id", userId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["circle-host-events"] });
      queryClient.invalidateQueries({ queryKey: ["circle-events"] });
      toast.success("Event cancelled");
    },
    onError: (error) => {
      console.error("Error cancelling event:", error);
      toast.error("Failed to cancel event");
    },
  });

  const updateEvent = useMutation({
    mutationFn: async ({ eventId, updates }: { eventId: string; updates: Partial<CreateCircleEventInput> }) => {
      if (!userId) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("circle_events")
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq("id", eventId)
        .eq("host_id", userId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["circle-host-events"] });
      queryClient.invalidateQueries({ queryKey: ["circle-events"] });
      toast.success("Event updated");
    },
    onError: (error) => {
      console.error("Error updating circle event:", error);
      toast.error("Failed to update event");
    },
  });

  return {
    hostedEvents: hostedEvents || [],
    isLoading,
    error,
    createEvent,
    cancelEvent,
    updateEvent,
  };
}
